import { Delete02Icon, GridIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, usePaginatedQuery, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { LayoutPanelTop } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { CreateCategoryGroupModal } from "@/components/modals/create-category-group-modal";
import { CreateCategoryModal } from "@/components/modals/create-category-modal";
import { EditCategoryGroupModal } from "@/components/modals/edit-category-group-modal";
import { EditCategoryModal } from "@/components/modals/edit-category-modal";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogMedia,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DisplaySelect } from "@/components/ui/display-select";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { SearchInput } from "@/components/ui/updated-search-input";
import { CategoriesTable } from "@/features/categories/_components/categories-table";
import { CategoryGroupCards } from "@/features/categories/_components/category-group-cards";
import { useHasPermission } from "@/hooks/use-has-permission";
import { useLocalStorageSettings } from "@/hooks/use-local-storage-settings";
import { useMe } from "@/hooks/use-me";
import { usePagination } from "@/hooks/use-pagination";
import { handleConvexError } from "@/lib/errors";
import type { CategoryGroupInput, CategoryInput } from "@/lib/schemas/category";
import { toast } from "@/lib/toast";
import type { Category, CategoryGroup } from "@/lib/types/category";

export const Route = createFileRoute("/(dashboard)/categories")({
	component: CategoriesPage,
});

const PAGE_SIZE = 10;

interface DisplaySettings {
	viewMode: "cards" | "rows";
	showHeaders: boolean;
	sortKey: string;
	columnVisibility: Record<string, boolean>;
}

const DEFAULT_SETTINGS: DisplaySettings = {
	viewMode: "rows",
	showHeaders: true,
	sortKey: "name",
	columnVisibility: {
		group: true,
		unit: true,
		taxRate: true,
	},
};

const CATEGORY_SORT_OPTIONS = [
	{ value: "name", label: "Name" },
	{ value: "createdAt", label: "Date Created" },
];

function CategoriesPage() {
	// Get current user for mutations that require orgId
	const me = useMe();
	const orgId = me?.activeOrganizationId;
	const canCreateGroup = useHasPermission("categoryGroups.create");
	const canUpdateGroup = useHasPermission("categoryGroups.update");

	// State
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedGroupId, setSelectedGroupId] =
		useState<Id<"categoryGroups"> | null>(null);

	const {
		settings: displaySettings,
		hasChanges,
		handleReset,
		handleSetDefault,
		updateSetting,
		updateColumnVisibility,
	} = useLocalStorageSettings<DisplaySettings>(
		"slappos_categories_display_settings",
		DEFAULT_SETTINGS,
		() => {
			const defaultMode =
				globalThis.window !== undefined && globalThis.innerWidth < 640
					? "cards"
					: "rows";
			return { ...DEFAULT_SETTINGS, viewMode: defaultMode };
		},
	);

	const { pagination, onPaginationChange, resetPage } =
		usePagination(PAGE_SIZE);
	// Modal state

	const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
	const [editCategoryOpen, setEditCategoryOpen] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState<Category | null>(
		null,
	);
	const [createGroupOpen, setCreateGroupOpen] = useState(false);
	const [editGroupOpen, setEditGroupOpen] = useState(false);
	const [selectedGroup, setSelectedGroup] = useState<CategoryGroup | null>(
		null,
	);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
		null,
	);

	// Mutations
	const createCategory = useMutation(api.features.categories.web.create);
	const updateCategory = useMutation(api.features.categories.web.update);
	const deleteCategory = useMutation(api.features.categories.web.remove);
	const createCategoryGroup = useMutation(
		api.features.categoryGroups.web.create,
	);
	const updateCategoryGroup = useMutation(
		api.features.categoryGroups.web.update,
	);

	// Mutation loading states
	const [isCreatingCategory, setIsCreatingCategory] = useState(false);
	const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
	const [isDeletingCategory, setIsDeletingCategory] = useState(false);
	const [isCreatingGroup, setIsCreatingGroup] = useState(false);
	const [isUpdatingGroup, setIsUpdatingGroup] = useState(false);

	// Queries
	const categoriesQuery = usePaginatedQuery(
		api.features.categories.web.list,
		{},
		{ initialNumItems: 50 },
	);
	const groupsQuery = useQuery(api.features.categoryGroups.web.list, {});

	const allCategories = categoriesQuery.results ?? [];
	const groups = groupsQuery ?? [];
	const isLoading =
		categoriesQuery.status === "LoadingFirstPage" || groupsQuery === undefined;

	// Load more handler for pagination
	const handleLoadMore = useCallback(() => {
		if (categoriesQuery.status === "CanLoadMore") {
			categoriesQuery.loadMore(50);
		}
	}, [categoriesQuery]);

	// Filter categories based on search, group
	const filteredCategories = useMemo(() => {
		let result = allCategories;

		// Filter by group
		if (selectedGroupId) {
			result = result.filter((c) => c.categoryGroupId === selectedGroupId);
		}

		// Filter by search
		if (searchQuery) {
			const query = searchQuery.toLowerCase();
			result = result.filter((c) => c.name.toLowerCase().includes(query));
		}

		return [...result].sort((a, b) => {
			if (displaySettings.sortKey === "name") {
				return a.name.localeCompare(b.name);
			}
			if (displaySettings.sortKey === "createdAt") {
				return (b._creationTime || 0) - (a._creationTime || 0);
			}
			return 0;
		});
	}, [searchQuery, selectedGroupId, allCategories, displaySettings.sortKey]);

	// Paginate the filtered results (client-side)
	const paginatedCategories = useMemo(() => {
		const start = pagination.pageIndex * pagination.pageSize;
		const end = start + pagination.pageSize;
		return filteredCategories.slice(start, end);
	}, [filteredCategories, pagination.pageIndex, pagination.pageSize]);

	// Calculate category counts per group
	const categoryCounts = useMemo(() => {
		const counts: Record<string, number> = {};
		allCategories.forEach((category) => {
			if (category.categoryGroupId) {
				counts[category.categoryGroupId] =
					(counts[category.categoryGroupId] ?? 0) + 1;
			}
		});
		return counts;
	}, [allCategories]);

	// Handlers
	const handleSearchChange = useCallback(
		(value: string) => {
			setSearchQuery(value);
			resetPage();
		},
		[resetPage],
	);

	const handleGroupSelect = useCallback(
		(groupId: Id<"categoryGroups"> | null) => {
			setSelectedGroupId(groupId);
			resetPage();
		},
		[resetPage],
	);

	const handleEdit = useCallback((category: Category) => {
		setSelectedCategory(category);
		setEditCategoryOpen(true);
	}, []);

	const handleDuplicate = useCallback(
		async (category: Category) => {
			if (!orgId) return;

			setIsCreatingCategory(true);
			try {
				await createCategory({
					orgId,
					name: `${category.name} (Copy)`,
					color: category.color,
					unitType: category.unitType,
					categoryGroupId: category.categoryGroupId,
					quantityPerUnit: category.quantityPerUnit,
					taxRate: category.taxRate,
					lowStockThreshold: category.lowStockThreshold,
				});
				toast.success(<>Category duplicated</>);
			} catch (error) {
				handleConvexError(error, "Failed to duplicate category");
			} finally {
				setIsCreatingCategory(false);
			}
		},
		[orgId, createCategory],
	);

	const handleDelete = useCallback((category: Category) => {
		setCategoryToDelete(category);
		setDeleteDialogOpen(true);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!categoryToDelete) return;

		setIsDeletingCategory(true);
		try {
			await deleteCategory({ orgId, categoryId: categoryToDelete._id });
			toast.success(<>Category deleted</>);
			setDeleteDialogOpen(false);
			setCategoryToDelete(null);
		} catch (error) {
			handleConvexError(error, "Failed to delete category");
		} finally {
			setIsDeletingCategory(false);
		}
	}, [categoryToDelete, orgId, deleteCategory]);

	const handleCreateCategory = useCallback(
		async (data: CategoryInput) => {
			if (!orgId) return;

			setIsCreatingCategory(true);
			try {
				await createCategory({
					orgId,
					name: data.name,
					color: data.color,
					unitType: data.unitType,
					categoryGroupId: data.categoryGroupId as
						| Id<"categoryGroups">
						| undefined,
					quantityPerUnit: data.quantityPerUnit,
					taxRate: data.taxRate,
					lowStockThreshold: data.lowStockThreshold,
				});
				toast.success(<>Category created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create category");
				throw error;
			} finally {
				setIsCreatingCategory(false);
			}
		},
		[orgId, createCategory],
	);

	const handleUpdateCategory = useCallback(
		async (data: CategoryInput) => {
			if (!orgId || !selectedCategory) return;

			setIsUpdatingCategory(true);
			try {
				await updateCategory({
					orgId,
					categoryId: selectedCategory._id,
					name: data.name,
					color: data.color,
					unitType: data.unitType,
					categoryGroupId: data.categoryGroupId as
						| Id<"categoryGroups">
						| undefined,
					quantityPerUnit: data.quantityPerUnit,
					taxRate: data.taxRate,
					lowStockThreshold: data.lowStockThreshold,
				});
				toast.success(<>Category updated</>);
			} catch (error) {
				handleConvexError(error, "Failed to update category");
				throw error;
			} finally {
				setIsUpdatingCategory(false);
			}
		},
		[orgId, selectedCategory, updateCategory],
	);

	const handleCreateGroup = useCallback(
		async (data: CategoryGroupInput) => {
			if (!orgId) return;

			setIsCreatingGroup(true);
			try {
				await createCategoryGroup({
					orgId,
					name: data.name,
				});
				toast.success(<>Group created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create group");
				throw error;
			} finally {
				setIsCreatingGroup(false);
			}
		},
		[orgId, createCategoryGroup],
	);

	const handleEditGroup = useCallback((group: CategoryGroup) => {
		setSelectedGroup(group);
		setEditGroupOpen(true);
	}, []);

	const handleUpdateGroup = useCallback(
		async (data: CategoryGroupInput) => {
			if (!orgId || !selectedGroup) return;

			setIsUpdatingGroup(true);
			try {
				await updateCategoryGroup({
					orgId,
					categoryGroupId: selectedGroup._id,
					name: data.name,
				});
				toast.success(<>Group updated</>);
			} catch (error) {
				handleConvexError(error, "Failed to update group");
				throw error;
			} finally {
				setIsUpdatingGroup(false);
			}
		},
		[orgId, selectedGroup, updateCategoryGroup],
	);

	// DisplaySelect configuration
	const displaySelectOptions = useMemo(
		() => [
			{
				key: "showHeaders",
				label: "Show table headers",
				checked: displaySettings.showHeaders,
				icon: <LayoutPanelTop className="size-4" />,
			},
		],
		[displaySettings.showHeaders],
	);

	const displaySelectProperties = useMemo(
		() => [
			{
				key: "group",
				label: "Group",
				visible: displaySettings.columnVisibility.group,
			},
			{
				key: "unit",
				label: "Unit Type",
				visible: displaySettings.columnVisibility.unit,
			},
			{
				key: "taxRate",
				label: "Tax Rate",
				visible: displaySettings.columnVisibility.taxRate,
			},
		],
		[displaySettings.columnVisibility],
	);

	const handleOptionsChange = useCallback(
		(key: string, checked: boolean) => {
			if (key === "showHeaders") updateSetting("showHeaders", checked);
		},
		[updateSetting],
	);

	return (
		<PageContent
			title="Categories"
			controls={
				<Can scope="categories.create">
					<ActionHeaderButton
						label="Add Category"
						onClick={() => setCreateCategoryOpen(true)}
					/>
				</Can>
			}
		>
			<div className="flex flex-1 flex-col gap-4 sm:gap-6">
				{/* Category Group Cards */}
				<CategoryGroupCards
					groups={groups as CategoryGroup[]}
					categoryCounts={categoryCounts}
					totalCount={allCategories.length}
					selectedGroupId={selectedGroupId}
					onSelectGroup={handleGroupSelect}
					onAddGroup={
						canCreateGroup ? () => setCreateGroupOpen(true) : undefined
					}
					onEditGroup={canUpdateGroup ? handleEditGroup : undefined}
				/>

				{/* Search and Filters */}
				{!isLoading && (allCategories.length > 0 || searchQuery) && (
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
						<div className="flex-1 sm:max-w-sm">
							<SearchInput
								placeholder="Search categories..."
								value={searchQuery}
								onChange={(e) => handleSearchChange(e.target.value)}
							/>
						</div>
						<div className="flex items-center gap-3">
							<DisplaySelect
								viewMode={displaySettings.viewMode}
								onViewModeChange={(mode) => updateSetting("viewMode", mode)}
								selectedSort={displaySettings.sortKey}
								onSortChange={(sort) => updateSetting("sortKey", sort)}
								sortOptions={CATEGORY_SORT_OPTIONS}
								options={displaySelectOptions}
								onOptionsChange={handleOptionsChange}
								properties={displaySelectProperties}
								onPropertiesChange={updateColumnVisibility}
								onReset={handleReset}
								onSetDefault={handleSetDefault}
								showFooter={hasChanges}
							/>
						</div>
					</div>
				)}

				{/* Categories Table or Empty State */}
				{!isLoading && filteredCategories.length === 0 ? (
					<div className="flex flex-1 flex-col justify-end pb-12">
						<EmptyStateWithSkeleton
							title="No categories found"
							description="Create categories to organize your products and manage inventory more effectively"
							actionLabel="Add Category"
							onAction={() => setCreateCategoryOpen(true)}
							icon={
								<HugeiconsIcon
									icon={GridIcon}
									className="h-5 w-5 text-neutral-700"
									strokeWidth={2.2}
								/>
							}
						/>
					</div>
				) : (
					<CategoriesTable
						data={paginatedCategories as Category[]}
						groups={groups as CategoryGroup[]}
						loading={isLoading}
						pagination={pagination}
						onPaginationChange={onPaginationChange}
						rowCount={filteredCategories.length}
						onEdit={handleEdit}
						onDuplicate={handleDuplicate}
						onDelete={handleDelete}
						viewMode={displaySettings.viewMode}
						showHeaders={displaySettings.showHeaders}
						columnVisibility={displaySettings.columnVisibility}
					/>
				)}

				{/* Load More Button */}
				{categoriesQuery.status === "CanLoadMore" && (
					<div className="flex justify-center">
						<Button variant="outline" onClick={handleLoadMore}>
							Load More
						</Button>
					</div>
				)}
			</div>

			{/* Modals */}
			<CreateCategoryModal
				open={createCategoryOpen}
				onOpenChange={setCreateCategoryOpen}
				groups={groups as CategoryGroup[]}
				onSubmit={handleCreateCategory}
				isSubmitting={isCreatingCategory}
			/>

			<EditCategoryModal
				open={editCategoryOpen}
				onOpenChange={setEditCategoryOpen}
				category={selectedCategory}
				groups={groups as CategoryGroup[]}
				onSubmit={handleUpdateCategory}
				isSubmitting={isUpdatingCategory}
			/>

			<CreateCategoryGroupModal
				open={createGroupOpen}
				onOpenChange={setCreateGroupOpen}
				onSubmit={handleCreateGroup}
				isSubmitting={isCreatingGroup}
			/>

			<EditCategoryGroupModal
				open={editGroupOpen}
				onOpenChange={setEditGroupOpen}
				group={selectedGroup}
				onSubmit={handleUpdateGroup}
				isSubmitting={isUpdatingGroup}
			/>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogMedia className="bg-red-50 text-red-600">
							<HugeiconsIcon icon={Delete02Icon} strokeWidth={2} />
						</AlertDialogMedia>
						<AlertDialogTitle>Delete category?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete{" "}
							<span className="font-medium text-neutral-900">
								"{categoryToDelete?.name}"?
							</span>{" "}
							This will remove the category from all associated products. This
							action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeletingCategory}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDelete}
							disabled={isDeletingCategory}
							className="rounded-xl px-4"
						>
							{isDeletingCategory ? "Deleting..." : "Delete Category"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContent>
	);
}
